import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { UsersService } from '../../../core/services/users.service';
import { SDiContactsService } from '../../../core/services/sdi-contacts.service';
import { PartnerService } from '../../../core/services/partner.service';
import { SDiContact } from '../../../core/models/sdi-contact.model';
import { Partner } from '../../../core/models/partner.model';
import { USER_TYPE_OPTIONS } from '../../../core/models/user.model';
import { PopupInfoService } from '../../../shared/services/popup-info.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faUser,
  faBuilding,
  faUniversity,
  faLock,
  faCircleInfo,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';

import {
  CustomSelectComponent,
  SelectOption,
} from '../../../shared/components/custom-select/custom-select.component';

@Component({
  selector: 'app-users-form-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FontAwesomeModule, CustomSelectComponent],
  templateUrl: './users-form-page.html',
  styleUrls: ['./users-form-page.scss'],
})
export class UsersFormPage implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly usersService = inject(UsersService);
  private readonly sdiContactsService = inject(SDiContactsService);
  private readonly partnerService = inject(PartnerService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly popupInfoService = inject(PopupInfoService);

  // Icons
  faUser = faUser;
  faBuilding = faBuilding;
  faUniversity = faUniversity;
  faLock = faLock;
  faCircleInfo = faCircleInfo;
  faXmark = faXmark;

  showRelationshipTypeInfoModal = false;

  userForm: FormGroup;
  userId: string | null = null;
  isEditMode = false;
  isLoading = false;
  isFetching = false;
  isPartnersLoading = false;
  isSDiContactsLoading = false;
  error: string | null = null;
  sdiContacts: SDiContact[] = [];
  partners: Partner[] = [];
  partnerOptions: SelectOption[] = [];
  partnersPage = 1;
  partnersTotalPages = 1;
  partnersSearch = '';
  loadingMorePartners = false;

  // Data Partner
  company_name = '';
  fiscal_address = '';
  iban = '';
  createdate = '';
  fixed_commission = 0;
  recurring_commission = 0;

  typeOptions = USER_TYPE_OPTIONS;

  constructor() {
    this.userForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      partner_id: [''],
      sdi_contact_id: [''],
      roles: [['partner'], Validators.required],
      type: ['', Validators.required],
    });
  }

  get f() {
    return this.userForm.controls;
  }

  ngOnInit(): void {
    this.userId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.userId;

    this.loadPartners();
    this.loadSDiContacts();

    if (this.isEditMode && this.userId) {
      this.loadUser(this.userId);
    }

    // Escuchar cambios en el partner_id para actualizar los datos de la empresa
    this.userForm.get('partner_id')?.valueChanges.subscribe((partnerId) => {
      this.onPartnerChange(partnerId);
    });
  }

  onPartnerChange(partnerId: string | number): void {
    const selectedPartner = this.partners.find((p) => String(p.id) === String(partnerId));
    if (selectedPartner) {
      this.company_name = selectedPartner.company_name;
      this.fiscal_address = selectedPartner.company_address || '';
      this.iban = selectedPartner.company_iban || '';
      this.createdate = selectedPartner.company_createdate
        ? new Date(selectedPartner.company_createdate).toLocaleDateString()
        : '';
      this.fixed_commission = selectedPartner.fixed_commission || 0;
      this.recurring_commission = selectedPartner.recurring_commission || 0;
    } else {
      // Opcional: Limpiar campos si no hay partner seleccionado
      this.company_name = '';
      this.fiscal_address = '';
      this.iban = '';
      this.createdate = '';
      this.fixed_commission = 0;
      this.recurring_commission = 0;
    }
  }

  loadPartners(append = false): void {
    if (this.isPartnersLoading || (append && this.partnersPage >= this.partnersTotalPages)) return;

    if (append) {
      this.loadingMorePartners = true;
    } else {
      this.isPartnersLoading = true;
      this.partnersPage = 1;
    }

    this.partnerService
      .getPartners({
        page: append ? this.partnersPage + 1 : 1,
        per_page: 30,
        search: this.partnersSearch,
      })
      .subscribe({
        next: (res) => {
          const newOptions = res.data.map((p) => ({
            value: String(p.id),
            label: p.company_name,
          }));

          if (append) {
            this.partners = [...this.partners, ...res.data];
            this.partnerOptions = [...this.partnerOptions, ...newOptions];
          } else {
            this.partners = res.data;
            this.partnerOptions = newOptions;
          }

          this.partnersPage = res.meta.current_page;
          this.partnersTotalPages = res.meta.last_page;
          this.isPartnersLoading = false;
          this.loadingMorePartners = false;
        },
        error: (err) => {
          console.error('Error loading partners:', err);
          this.popupInfoService.showError('Error al cargar la lista de partners');
          this.isPartnersLoading = false;
          this.loadingMorePartners = false;
        },
      });
  }

  onPartnerSearch(term: string): void {
    this.partnersSearch = term;
    this.loadPartners(false);
  }

  onPartnerLoadMore(): void {
    this.loadPartners(true);
  }

  loadSDiContacts(): void {
    this.isSDiContactsLoading = true;
    this.sdiContactsService.list({ per_page: 1000 }).subscribe({
      next: (res) => {
        this.sdiContacts = res.data;
        this.isSDiContactsLoading = false;
      },
      error: (err) => {
        console.error('Error loading SDi contacts:', err);
        this.popupInfoService.showError('Error al cargar los contactos disponibles en SDi');
        this.isSDiContactsLoading = false;
      },
    });
  }

  loadUser(id: string): void {
    this.isFetching = true;
    this.usersService.getById(id).subscribe({
      next: (res) => {
        const user = res.data;
        this.userForm.patchValue({
          name: user.name,
          email: user.email,
          phone: user.phone || '',
          partner_id: user.partner?.id || '',
          sdi_contact_id: user.sdi_contact?.id || '',
          roles: user.roles,
          type: user.type || '',
        });

        // Asegurarse de que el partner seleccionado esté en la lista de opciones y en los datos
        if (
          user.partner &&
          !this.partnerOptions.find((o) => o.value === String(user.partner?.id))
        ) {
          this.partnerOptions = [
            { value: String(user.partner.id), label: user.partner.company_name },
            ...this.partnerOptions,
          ];
          this.partners = [user.partner, ...this.partners];
        }

        this.company_name = user.partner?.company_name || '';
        this.fiscal_address = user.partner?.company_address || '';
        this.iban = user.partner?.company_iban || '';
        this.createdate = user.partner?.company_createdate
          ? new Date(user.partner.company_createdate).toLocaleDateString()
          : '';
        this.fixed_commission = user.partner?.fixed_commission || 0;
        this.recurring_commission = user.partner?.recurring_commission || 0;
        this.isFetching = false;
      },
      error: (err) => {
        this.error = 'Error al cargar el usuario';
        this.popupInfoService.showError('Error al cargar el usuario');
        this.isFetching = false;
        console.error('Error loading user:', err);
      },
    });
  }

  onSubmit(): void {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.error = null;

    const userData = this.userForm.value;

    // Generar contraseña aleatoria para nuevos usuarios
    if (!this.isEditMode) {
      userData.password = Math.random().toString(36).slice(-8);
    }

    const request =
      this.isEditMode && this.userId
        ? this.usersService.update(this.userId, userData)
        : this.usersService.create(userData);

    request.subscribe({
      next: () => {
        this.popupInfoService.showSuccess(
          this.isEditMode ? 'Usuario actualizado correctamente' : 'Usuario creado correctamente'
        );
      },
      error: () => {
        const errorMessage = this.isEditMode
          ? 'Error al actualizar el usuario, por favor inténtalo de nuevo más tarde.'
          : 'Error al crear el usuario, por favor inténtalo de nuevo más tarde.';
        const msg = errorMessage;
        this.error = msg;
        this.popupInfoService.showError(msg);
        this.isLoading = false;
      },
    });
  }

  onCancel(): void {
    this.router.navigate(['/admin/users']);
  }

  openRelationshipTypeInfoModal(): void {
    this.showRelationshipTypeInfoModal = true;
  }

  closeRelationshipTypeInfoModal(): void {
    this.showRelationshipTypeInfoModal = false;
  }

  onRoleChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const value = select.value;
    this.userForm.patchValue({ roles: [value] });
    if (value === 'admin') {
      this.userForm.patchValue({
        sdi_contact_id: '',
        partner_id: '',
      });
    }
  }
}
